import { ApiClient, type RunAction } from './api/client';
import { ApiError } from './api/errors';
import type { TurnstileTokenProvider } from './turnstile/provider';
import type {
  AbuseSignalRecord,
  ActionResult,
  AdminConfigRecord,
  CosmeticClaimCampaignStatus,
  EquipmentResponse,
  EquipSlot,
  HubFacing,
  CosmeticFounderClaimStatus,
  InventoryItem,
  InventoryResponse,
  RunState,
  SolanaTokenBalance,
  SpectateFrame,
  SpinReward,
  StartRunResult,
  SupplyKey,
} from './api/types';
import type { CashoutIntentPayment, MarketIntentPayment, MarketRarityFilter, PendingBindEquip, SpectateSession } from './state/store';
import { OBOL_CONTRACT_ADDRESS } from './api/obolToken';
import { formatRawTokenAmount, parseTokenAmountToRaw } from './ui/format';
import { authenticate } from './auth/authFlow';
import { clearSession, loadSession, saveSession } from './auth/session';
import { DevKeypairWallet, PhantomWallet, type Wallet } from './auth/wallet';
import { WALLET_CHANGED_MESSAGE } from './auth/walletWatcher';
import { initialState, Store, type AppState, type TerminalResult } from './state/store';
import { browserSolanaPayments, type SolanaWalletPayments } from './solana/walletPayment';
import {
  localHubPosition,
  moveHubPosition,
  nearbyStationForPosition,
  spawnHubPosition,
  type HubDirection,
  type HubPosition,
} from './ui/waystationPlaza';
import { describeItem, slotLabel } from './inventory/format';

const ACTIVE_RUN_KEY = 'gravelight.activeRunId';
const ADMIN_TOKEN_KEY = 'gravelight.adminToken';
const FOUNDER_CLAIM_CAMPAIGN_ID = 'founder_waystation_pilgrim_01';

export interface ControllerOptions {
  onRunActionResult?: (action: RunAction, prev: RunState, result: ActionResult) => void;
  /**
   * Cloudflare Turnstile token provider for protected actions. Absent (dev/
   * test without a site key) protected requests are sent without a token —
   * the backend's off/shadow modes accept that; enforce mode requires the
   * deploy to configure both site key and secret.
   */
  turnstile?: TurnstileTokenProvider;
  onSpectateFrame?: (prev: SpectateFrame | null, current: SpectateFrame) => void;
  onSpectateReset?: () => void;
  solanaPayments?: Partial<SolanaWalletPayments>;
  cashoutSubmitRetryDelaysMs?: number[];
  spinnerSubmitRetryDelaysMs?: number[];
  marketSubmitRetryDelaysMs?: number[];
}

const DEFAULT_SPINNER_SUBMIT_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 6_000];
// Marketplace settlement waits out normal Solana propagation after the wallet
// already sent the payment: 2s, 3s, then every 5s for a ~65s total window.
const DEFAULT_MARKET_SUBMIT_RETRY_DELAYS_MS = [2_000, 3_000, ...Array<number>(12).fill(5_000)];

function normalizeControllerError(err: unknown): ApiError {
  return err instanceof ApiError
    ? err
    : new ApiError({ status: 0, code: 'client_error', message: err instanceof Error ? err.message : String(err) });
}

function inventoryItemTitle(item: InventoryItem | undefined): string {
  return item ? describeItem(item.item).title : 'item';
}

function inventorySelection(current: string | null | undefined, items: InventoryItem[]): string | null {
  if (current && items.some((entry) => entry.itemInstanceId === current)) return current;
  return items[0]?.itemInstanceId ?? null;
}

function equipStateMismatchError(item: InventoryItem | undefined, slot: EquipSlot): ApiError {
  return new ApiError({
    status: 409,
    code: 'equip_state_mismatch',
    message: `Equip was accepted, but refreshed equipment did not show ${inventoryItemTitle(item)} in ${slotLabel(slot)}.`,
  });
}

function pendingBindEquip(itemInstanceId: string, slot: EquipSlot, item: InventoryItem | undefined): PendingBindEquip {
  return {
    itemInstanceId,
    slot,
    title: inventoryItemTitle(item),
  };
}

function loadActiveRunId(): string | null {
  try { return localStorage.getItem(ACTIVE_RUN_KEY); } catch { return null; }
}
function saveActiveRunId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_RUN_KEY, id);
    else localStorage.removeItem(ACTIVE_RUN_KEY);
  } catch { /* storage unavailable */ }
}
const MARKET_INTENT_TX_KEY = 'gravelight.marketIntentTx';
/** Keep an unsettled marketplace payment signature recoverable for 6 hours. */
const MARKET_INTENT_TX_MAX_AGE_MS = 6 * 60 * 60 * 1000;

interface StoredMarketIntentTx {
  signature: string;
  listingKind: 'gear' | 'cosmetic';
  savedAt: number;
}

/**
 * Wallet-sent marketplace payment signatures survive a page refresh so the
 * buyer never has to hunt Solscan for a signature they already produced.
 * Stale entries (settled long ago / abandoned) age out on read.
 */
function loadStoredMarketIntentTx(): Record<string, StoredMarketIntentTx> {
  try {
    const raw = localStorage.getItem(MARKET_INTENT_TX_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, StoredMarketIntentTx>;
    const fresh: Record<string, StoredMarketIntentTx> = {};
    for (const [intentId, entry] of Object.entries(parsed)) {
      if (
        entry && typeof entry.signature === 'string' && entry.signature.length > 0 &&
        typeof entry.savedAt === 'number' && Date.now() - entry.savedAt < MARKET_INTENT_TX_MAX_AGE_MS
      ) {
        fresh[intentId] = {
          signature: entry.signature,
          listingKind: entry.listingKind === 'cosmetic' ? 'cosmetic' : 'gear',
          savedAt: entry.savedAt,
        };
      }
    }
    return fresh;
  } catch { return {}; }
}

function saveStoredMarketIntentTx(map: Record<string, StoredMarketIntentTx>): void {
  try {
    if (Object.keys(map).length === 0) localStorage.removeItem(MARKET_INTENT_TX_KEY);
    else localStorage.setItem(MARKET_INTENT_TX_KEY, JSON.stringify(map));
  } catch { /* storage unavailable — in-memory state still covers the session */ }
}

const CASHOUT_INTENT_TX_KEY = 'gravelight.cashoutIntentTx';

interface StoredCashoutIntentTx {
  signature: string;
  savedAt: number;
}

/** Gold Exchange twin of the marketplace signature store (same 6h age-out). */
function loadStoredCashoutIntentTx(): Record<string, StoredCashoutIntentTx> {
  try {
    const raw = localStorage.getItem(CASHOUT_INTENT_TX_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, StoredCashoutIntentTx>;
    const fresh: Record<string, StoredCashoutIntentTx> = {};
    for (const [intentId, entry] of Object.entries(parsed)) {
      if (
        entry && typeof entry.signature === 'string' && entry.signature.length > 0 &&
        typeof entry.savedAt === 'number' && Date.now() - entry.savedAt < MARKET_INTENT_TX_MAX_AGE_MS
      ) {
        fresh[intentId] = { signature: entry.signature, savedAt: entry.savedAt };
      }
    }
    return fresh;
  } catch { return {}; }
}

function saveStoredCashoutIntentTx(map: Record<string, StoredCashoutIntentTx>): void {
  try {
    if (Object.keys(map).length === 0) localStorage.removeItem(CASHOUT_INTENT_TX_KEY);
    else localStorage.setItem(CASHOUT_INTENT_TX_KEY, JSON.stringify(map));
  } catch { /* storage unavailable — in-memory state still covers the session */ }
}

function loadAdminToken(): string | null {
  try { return localStorage.getItem(ADMIN_TOKEN_KEY); } catch { return null; }
}
function saveAdminToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
    else localStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch { /* storage unavailable */ }
}

/**
 * Orchestrates the playtest client: drives the API client, persists the session,
 * and pushes results into the {@link Store}. Every network op runs through `run()`,
 * which manages the busy flag and funnels API errors into a visible (never hidden)
 * error state.
 */
export class Controller {
  private rewardLoadoutManuallyAdjusted = false;
  /** Intents with a settlement retry loop running — a second loop would double-submit. */
  private readonly marketSettleInFlight = new Set<string>();
  private readonly cashoutSettleInFlight = new Set<string>();

  constructor(
    private readonly store: Store,
    private readonly client: ApiClient,
    private readonly options: ControllerOptions = {},
  ) {}

  // ── lifecycle ──────────────────────────────────────────────────────────────

  /** Restore a persisted session (if any) and reload the dependent state. */
  async init(): Promise<void> {
    const requestedView = this.store.get().view;
    const adminToken = loadAdminToken();
    if (adminToken) this.store.set({ adminToken });

    const session = loadSession();
    if (!session) {
      if (requestedView === 'admin' && adminToken) await this.openAdmin();
      return;
    }
    this.store.set({
      session: session.token,
      walletKind: session.walletKind,
      walletAddress: session.walletAddress,
      view: requestedView === 'admin' ? 'admin' : 'waystation',
    });
    await this.refreshAfterAuth();
    await this.resumeActiveRun();
    if (requestedView === 'admin') await this.openAdmin();
  }

  async connectPhantom(): Promise<void> {
    await this.connectWith(() => PhantomWallet.connect());
  }

  async connectDevWallet(): Promise<void> {
    await this.connectWith(async () => DevKeypairWallet.loadOrCreate());
  }

  /** Forget the dev keypair and authenticate a brand-new dev account. */
  async newDevWallet(): Promise<void> {
    DevKeypairWallet.reset();
    await this.connectWith(async () => DevKeypairWallet.loadOrCreate());
  }

  async playFromHome(): Promise<void> {
    if (!this.store.get().session) {
      await this.connectPhantom();
      return;
    }

    this.store.set({ busy: 'entering Waystation', error: null, notice: null });
    try {
      await this.loadWaystationHub();
      this.store.set({ busy: null });
    } catch (err) {
      const normalized = normalizeControllerError(err);
      if (normalized.status !== 401) {
        this.store.set({ busy: null, error: normalized });
        return;
      }

      this.logout();
      this.store.set({ busy: null, error: null, notice: 'Session expired. Please reconnect wallet.' });
      await this.connectPhantom();
    }
  }

  private async connectWith(make: () => Promise<Wallet>): Promise<void> {
    await this.run('connecting wallet', async () => {
      const wallet = await make();
      const verified = await authenticate(wallet, this.client);
      saveSession({
        token: verified.session,
        accountId: verified.account_id,
        walletKind: wallet.kind,
        walletAddress: wallet.address,
      });
      this.store.set({
        session: verified.session,
        walletKind: wallet.kind,
        walletAddress: wallet.address,
        view: 'waystation',
        spectateSession: null,
        notice: verified.created ? 'New account created.' : 'Welcome back.',
      });
    });
    if (this.store.get().session) await this.refreshAfterAuth();
  }

  logout(notice = 'Signed out.'): void {
    clearSession();
    saveActiveRunId(null);
    // Pending wallet-payment signature caches are wallet-specific; they must
    // not survive into another account's session. The SERVER keeps the intents
    // and their reconciliation authority — only the local recovery copy goes.
    saveStoredMarketIntentTx({});
    saveStoredCashoutIntentTx({});
    this.rewardLoadoutManuallyAdjusted = false;
    const {
      apiBase,
      devMode,
      adminToken,
      adminConfig,
      adminAbuseSignals,
      adminSelectedAbuseSignal,
      adminReplayResult,
      adminOpsSummary,
    } = this.store.get();
    // Full reset to the logged-out baseline (view: landing) so no per-account
    // state — including in-memory pending payment maps — leaks across wallets.
    // Only the admin console (token-gated separately) survives a sign-out.
    this.store.set({
      ...initialState(apiBase, devMode),
      adminToken, adminConfig, adminAbuseSignals, adminSelectedAbuseSignal, adminReplayResult,
      adminOpsSummary,
      notice,
    });
  }

  /**
   * The Phantom-connected account drifted away from the session-bound wallet
   * (accountChanged/disconnect event or focus-check mismatch — see
   * auth/walletWatcher.ts). Never rebinds silently: the player must complete
   * a fresh signed login challenge as the new account.
   */
  logoutForWalletChange(): void {
    if (!this.store.get().session) return;
    this.logout(WALLET_CHANGED_MESSAGE);
  }

  // ── status panels ───────────────────────────────────────────────────────────

  private async refreshAfterAuth(): Promise<void> {
    await this.refreshAccount();
    await this.refreshEligibilityStatus();
    await this.refreshPass();
    await this.loadInventory();
    await this.refreshMerchant({ swallow: true });
    // Land in the Waystation home base after auth (resumeActiveRun may override
    // to the run view if a run is still active). Best-effort — a hub hiccup must
    // not block sign-in.
    await this.enterWaystation({ swallow: true });
  }

  async refreshAccount(): Promise<void> {
    await this.run('loading account', async () => {
      const account = await this.client.accountMe();
      this.store.set({ account });
    }, { onAuthError: () => this.logout() });
  }

  /** Read-only eligibility status (no RPC call). */
  async refreshEligibilityStatus(): Promise<void> {
    await this.run('loading eligibility', async () => {
      const status = await this.client.eligibilityStatus();
      this.store.set({ eligibility: { ...status } });
    });
  }

  /** Force a live eligibility re-check (does the gate RPC read + state transition). */
  async refreshEligibility(): Promise<void> {
    await this.run('checking eligibility', async () => {
      const refresh = await this.client.eligibilityRefresh(await this.turnstileToken('eligibility_refresh'));
      const status = await this.client.eligibilityStatus();
      this.store.set({
        eligibility: { ...status, state: refresh.state, balance: refresh.balance, activated: refresh.activated },
        notice: `Eligibility: ${refresh.state} (${refresh.eligibility_state}).`,
      });
      // The spinner's free-spin gate mirrors eligibility — refresh it in place.
      if (this.store.get().view === 'spinner') {
        this.store.set({ spinner: await this.client.spinnerStatus() });
      }
    });
    await this.refreshPass();
  }

  async refreshPass(): Promise<void> {
    await this.run('loading passes', async () => {
      const pass = await this.client.passStatus();
      this.store.set({ pass });
    });
  }

  async openMerchant(): Promise<void> {
    await this.refreshMerchant();
    this.store.set({ view: 'merchant' });
  }

  async refreshMerchant(opts: { swallow?: boolean } = {}): Promise<void> {
    await this.run('loading Merchant', async () => {
      const merchant = await this.client.merchantStatus();
      const current = this.store.get().rewardLoadoutExtras;
      this.store.set({
        merchant,
        rewardLoadoutExtras: this.normalizeRewardLoadout(current, merchant.supplies, merchant.extraCaps, {
          autofill: !this.rewardLoadoutManuallyAdjusted,
        }),
      });
    }, opts);
  }

  async buySupply(supplyKey: SupplyKey, quantity: number): Promise<void> {
    await this.run(`buying ${supplyKey}`, async () => {
      const result = await this.client.merchantBuySupply(supplyKey, quantity);
      const current = this.store.get().merchant;
      const merchant = current
        ? { ...current, goldBalance: result.goldBalance, supplies: result.supplies }
        : await this.client.merchantStatus();
      this.store.set({
        merchant,
        rewardLoadoutExtras: this.normalizeRewardLoadout(this.store.get().rewardLoadoutExtras, merchant.supplies, merchant.extraCaps, {
          autofill: !this.rewardLoadoutManuallyAdjusted,
        }),
        notice: `${supplyKey} stock updated.`,
      });
    });
  }

  async repairItem(itemInstanceId: string): Promise<void> {
    await this.run('repairing item', async () => {
      const result = await this.client.merchantRepairItem(itemInstanceId);
      const current = this.store.get().merchant;
      const repairableItems = current?.repairableItems.filter((item) => item.itemInstanceId !== result.item.itemInstanceId) ?? [];
      this.store.set({
        merchant: current ? { ...current, goldBalance: result.goldBalance, repairableItems } : await this.client.merchantStatus(),
        notice: 'Item repaired.',
      });
      await this.loadInventory();
    });
  }

  async salvageItem(itemInstanceId: string): Promise<void> {
    await this.run('salvaging item', async () => {
      const result = await this.client.merchantSalvageItem(itemInstanceId);
      const current = this.store.get().merchant;
      const salvageItems = current?.salvageItems.filter((item) => item.itemInstanceId !== result.itemInstanceId) ?? [];
      const repairableItems = current?.repairableItems.filter((item) => item.itemInstanceId !== result.itemInstanceId) ?? [];
      const salvageIneligibleItems =
        current?.salvageIneligibleItems.filter((item) => item.itemInstanceId !== result.itemInstanceId) ?? [];
      this.store.set({
        merchant: current
          ? { ...current, goldBalance: result.goldBalance, repairableItems, salvageItems, salvageIneligibleItems }
          : await this.client.merchantStatus(),
      });
      await this.loadInventory();
      this.store.set({ notice: `Salvaged item for ${result.goldCredited} Gold.` });
    });
  }

  adjustRewardLoadout(supplyKey: SupplyKey, delta: number): void {
    const { merchant, rewardLoadoutExtras } = this.store.get();
    if (!merchant) return;
    const next = { ...rewardLoadoutExtras };
    const stock = merchant.supplies[supplyKey];
    const maxExtra = Math.max(0, Math.min(stock, merchant.extraCaps[supplyKey]));
    next[supplyKey] = Math.max(0, Math.min(maxExtra, next[supplyKey] + delta));
    this.rewardLoadoutManuallyAdjusted = true;
    this.store.set({ rewardLoadoutExtras: next });
  }

  private normalizeRewardLoadout(
    extras: { oil: number; torch: number; potion: number },
    stock: { oil: number; torch: number; potion: number },
    extraCaps: { oil: number; torch: number; potion: number },
    opts: { autofill: boolean } = { autofill: true },
  ): { oil: number; torch: number; potion: number } {
    const maxExtra = (key: SupplyKey) => Math.max(0, Math.min(stock[key], extraCaps[key]));
    return {
      oil: opts.autofill ? maxExtra('oil') : Math.max(0, Math.min(maxExtra('oil'), extras.oil)),
      torch: opts.autofill ? maxExtra('torch') : Math.max(0, Math.min(maxExtra('torch'), extras.torch)),
      potion: opts.autofill ? maxExtra('potion') : Math.max(0, Math.min(maxExtra('potion'), extras.potion)),
    };
  }

  // ── admin config (Phase L1) ────────────────────────────────────────────────

  async openAdmin(): Promise<void> {
    const adminToken = this.store.get().adminToken ?? loadAdminToken();
    this.store.set({ view: 'admin', adminToken });
    const current = this.store.get();
    if (adminToken && (!current.adminConfig || !current.adminAbuseSignals || !current.adminOpsSummary)) {
      await this.refreshAdminData();
    }
  }

  async setAdminTokenAndLoad(rawToken: string): Promise<void> {
    const token = rawToken.trim();
    if (token.length === 0) {
      this.store.set({
        view: 'admin',
        busy: null,
        notice: null,
        error: { status: 400, code: 'missing_admin_token', message: 'Enter an admin API token.' },
      });
      return;
    }
    saveAdminToken(token);
    this.store.set({
      view: 'admin',
      adminToken: token,
      adminConfig: null,
      adminAbuseSignals: null,
      adminSelectedAbuseSignal: null,
      adminReplayResult: null,
      adminOpsSummary: null,
    });
    await this.refreshAdminData();
  }

  clearAdminToken(): void {
    saveAdminToken(null);
    this.store.set({
      adminToken: null,
      adminConfig: null,
      adminAbuseSignals: null,
      adminSelectedAbuseSignal: null,
      adminReplayResult: null,
      adminOpsSummary: null,
      view: 'admin',
      notice: 'Admin token cleared.',
      error: null,
    });
  }

  async refreshAdminData(): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    await this.run('loading admin data', async () => {
      const [config, abuse, ops] = await Promise.all([
        this.client.adminConfig(token),
        this.client.adminAbuse(token),
        this.client.adminOpsSummary(token),
      ]);
      this.store.set({
        view: 'admin',
        adminConfig: config.config,
        adminAbuseSignals: abuse.signals,
        adminOpsSummary: ops,
        notice: 'Admin config loaded.',
      });
    });
  }

  async refreshAdminConfig(): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    await this.run('loading admin config', async () => {
      const res = await this.client.adminConfig(token);
      this.store.set({ view: 'admin', adminConfig: res.config, notice: 'Admin config loaded.' });
    });
  }

  async saveAdminConfig(key: string, jsonText: string): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;

    let value: unknown;
    try {
      value = JSON.parse(jsonText);
    } catch {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 400, code: 'invalid_admin_json', message: 'Enter valid JSON before saving.' },
      });
      return;
    }

    await this.run(`saving ${key}`, async () => {
      const updated = await this.client.adminUpdateConfig(token, key, value);
      const current = this.store.get().adminConfig ?? [];
      this.store.set({
        adminConfig: replaceAdminConfigRecord(current, updated),
        notice: `Saved ${key}.`,
      });
    });
  }

  async refreshAdminAbuse(): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    await this.run('loading abuse signals', async () => {
      const res = await this.client.adminAbuse(token);
      this.store.set({ adminAbuseSignals: res.signals });
    });
  }

  async scanAdminAbuse(): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    await this.run('scanning abuse signals', async () => {
      const res = await this.client.adminAbuseScan(token);
      const signals = res.signals.length > 0 ? res.signals : (await this.client.adminAbuse(token)).signals;
      this.store.set({ adminAbuseSignals: signals, notice: `Abuse scan created ${res.created} signal(s).` });
    });
  }

  async openAdminAbuseSignal(id: number): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    await this.run('loading abuse signal', async () => {
      const res = await this.client.adminAbuseSignal(token, id);
      this.store.set({ adminSelectedAbuseSignal: res.signal });
    });
  }

  async reviewAdminAbuse(id: number): Promise<void> {
    await this.setAdminAbuseStatus(id, 'reviewed');
  }

  async dismissAdminAbuse(id: number): Promise<void> {
    await this.setAdminAbuseStatus(id, 'dismissed');
  }

  async flagAdminAbuse(id: number): Promise<void> {
    await this.setAdminAbuseStatus(id, 'flagged');
  }

  async replayAdminRun(rawRunId: string): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    const runId = rawRunId.trim();
    if (!runId) {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 400, code: 'missing_run_id', message: 'Enter a run id.' },
      });
      return;
    }
    await this.run('replaying run', async () => {
      const result = await this.client.adminReplayRun(token, runId);
      this.store.set({
        adminReplayResult: result,
        notice: result.ok ? `Replay OK for ${runId}.` : `Replay mismatch for ${runId}.`,
      });
    });
  }

  async refreshAdminOpsSummary(): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    await this.run('loading ops summary', async () => {
      const summary = await this.client.adminOpsSummary(token);
      this.store.set({ adminOpsSummary: summary, notice: 'Ops summary loaded.' });
    });
  }

  // ── runs ──────────────────────────────────────────────────────────────────

  async startEcho(): Promise<void> {
    await this.startRun('starting Echo run', () => this.client.startEcho());
  }

  async startReward(): Promise<void> {
    const extras = this.store.get().rewardLoadoutExtras;
    await this.startRun('starting Reward run', () => this.client.startReward({
      extraOil: extras.oil,
      extraTorch: extras.torch,
      extraPotion: extras.potion,
    }));
    if (!this.store.get().error) {
      // Keep the resume/stale-closed notice: the refreshes below reset it.
      const notice = this.store.get().notice;
      this.rewardLoadoutManuallyAdjusted = false;
      this.store.set({ rewardLoadoutExtras: { oil: 0, torch: 0, potion: 0 } });
      await this.refreshPass();
      await this.refreshMerchant({ swallow: true });
      if (notice) this.store.set({ notice });
    }
  }

  private async startRun(label: string, start: () => Promise<StartRunResult>): Promise<void> {
    await this.loadEquippedCosmeticsForRun();
    await this.run(label, async () => {
      try {
        const { runId, state } = await start();
        saveActiveRunId(runId);
        this.store.set({ run: state, runId, lastOutcome: null, lastEvents: [], terminal: null, view: 'run', notice: null });
      } catch (err) {
        // The server already has a live run for this account (e.g. this device
        // lost its local state). Re-attach to it instead of dead-ending.
        if (err instanceof ApiError && err.code === 'active_run' && typeof err.details?.run_id === 'string') {
          const runId = err.details.run_id;
          const res = await this.client.getRun(runId);
          if (res.status === 'active' && res.state) {
            saveActiveRunId(runId);
            this.store.set({
              run: res.state,
              runId,
              lastOutcome: null,
              lastEvents: [],
              terminal: null,
              view: 'run',
              notice: 'You already have an active run — resumed it.',
            });
            return;
          }
          saveActiveRunId(null);
        }
        if (err instanceof ApiError && err.code === 'stale_run_closed') {
          saveActiveRunId(null);
          this.store.set({
            runId: null,
            run: null,
            notice: 'Your previous run was closed after being idle. You can start a new run now.',
          });
          return;
        }
        throw err;
      }
    });
  }

  async act(action: RunAction): Promise<void> {
    const { runId, run: prev } = this.store.get();
    if (!runId) return;
    await this.run('action: ' + action.type, async () => {
      const result = await this.client.action(runId, action);
      this.applyActionResult(result);
      if (prev) this.options.onRunActionResult?.(action, prev, result);
    });
  }

  private applyActionResult(result: ActionResult): void {
    if (result.status === 'active') {
      this.store.set({ run: result.state, lastOutcome: result.outcome, lastEvents: result.events ?? [], error: null });
      return;
    }
    if (result.status === 'died') {
      const run = this.store.get().run;
      saveActiveRunId(null);
      const terminal: TerminalResult = {
        kind: 'died',
        cause: typeof result.outcome.cause === 'string' ? result.outcome.cause : undefined,
        floor: run?.floor ?? 0,
        carriedGoldLost: run?.carriedGold ?? 0,
        practiceOnly: run?.type === 'echo',
      };
      this.store.set({ terminal, runId: null, lastOutcome: result.outcome, lastEvents: result.events ?? [] });
      void this.refreshPass();
      return;
    }
    // extracted
    const run = this.store.get().run;
    saveActiveRunId(null);
    this.store.set({
      terminal: {
        kind: 'extracted',
        settlement: result.settlement,
        sealed: result.sealed,
        leftBehind: result.left_behind,
        practiceOnly: run?.type === 'echo',
        message: result.message,
      },
      runId: null,
      lastEvents: [],
    });
    void this.refreshPass();
    void this.loadInventory();
  }

  async abandonRun(): Promise<void> {
    const { runId, run } = this.store.get();
    if (!runId) return;
    await this.run('abandoning run', async () => {
      await this.client.abandon(runId);
      saveActiveRunId(null);
      this.store.set({
        terminal: { kind: 'abandoned', floor: run?.floor ?? 0, carriedGoldLost: run?.carriedGold ?? 0, practiceOnly: run?.type === 'echo' },
        runId: null,
        lastEvents: [],
      });
    });
    await this.refreshPass();
  }

  /**
   * On reload, re-attach to a still-active run. Local storage is only a hint —
   * the server's account-level discovery (GET /runs/active) is authoritative,
   * so a cleared cache or a new device can still find and resume the run.
   */
  async resumeActiveRun(): Promise<void> {
    let runId = loadActiveRunId();
    if (!runId) runId = await this.discoverServerActiveRun();
    if (!runId) return;
    await this.loadEquippedCosmeticsForRun();
    await this.run('resuming run', async () => {
      let res = await this.client.getRun(runId!);
      if (res.status !== 'active') {
        // Local id went terminal; the server may still hold a different
        // (newer) active run for this account — prefer its answer.
        saveActiveRunId(null);
        const serverRunId = await this.discoverServerActiveRun();
        if (serverRunId && serverRunId !== runId) {
          runId = serverRunId;
          res = await this.client.getRun(serverRunId);
        }
      }
      if (res.status === 'active' && res.state) {
        saveActiveRunId(runId!);
        this.store.set({ run: res.state, runId, view: 'run', terminal: null, lastEvents: [] });
      } else {
        saveActiveRunId(null);
        this.store.set({ runId: null, lastEvents: [], notice: `Previous run is ${res.status}.` });
      }
    }, { swallow: true });
  }

  /** Ask the backend which run (if any) is active for this account. */
  private async discoverServerActiveRun(): Promise<string | null> {
    try {
      const res = await this.client.getActiveRun();
      return res.active?.run_id ?? null;
    } catch {
      return null;
    }
  }

  dismissTerminal(): void {
    // Run result acknowledged → return to the Waystation home base and refresh presence.
    this.store.set({ terminal: null, run: null, lastOutcome: null, lastEvents: [], view: 'waystation' });
    void this.enterWaystation({ swallow: true });
  }

  // ── inventory / equipment ────────────────────────────────────────────────────

  private async inventorySnapshot(): Promise<{ inventory: InventoryResponse; equipment: EquipmentResponse }> {
    const [inventory, equipment] = await Promise.all([this.client.inventory(), this.client.equipment()]);
    return { inventory, equipment };
  }

  async loadInventory(): Promise<void> {
    await this.run('loading inventory', async () => {
      const { inventory, equipment } = await this.inventorySnapshot();
      this.store.set({
        inventory: inventory.items,
        equipment,
        selectedInventoryItemId: inventorySelection(this.store.get().selectedInventoryItemId, inventory.items),
      });
      // The owned cosmetics collection renders under the Stash. Best-effort: a
      // cosmetics failure must not take down item inventory — surface a
      // recoverable error instead.
      try {
        const mine = await this.client.myCosmetics();
        this.store.set({
          myCosmetics: mine.owned,
          equippedCosmetics: mine.equipped,
          cosmeticsGoldBalance: mine.goldBalance,
          inventoryCosmeticsError: null,
        });
      } catch {
        this.store.set({ inventoryCosmeticsError: 'Cosmetics collection could not be loaded.' });
      }
    });
  }

  async equip(itemInstanceId: string, slot: EquipSlot): Promise<void> {
    await this.run('equipping item', async () => {
      const knownItem = this.store.get().inventory?.find((entry) => entry.itemInstanceId === itemInstanceId);
      try {
        await this.equipAndRefresh(itemInstanceId, slot, false, knownItem);
      } catch (err) {
        const normalized = normalizeControllerError(err);
        if (normalized.code === 'bind_confirmation_required') {
          this.store.set({
            pendingBindEquip: pendingBindEquip(itemInstanceId, slot, knownItem),
            selectedInventoryItemId: itemInstanceId,
            error: null,
            notice: null,
          });
          return;
        }
        throw normalized;
      }
    });
  }

  cancelBindEquip(): void {
    this.store.set({ pendingBindEquip: null });
  }

  async confirmBindEquip(): Promise<void> {
    const pending = this.store.get().pendingBindEquip;
    if (!pending) return;
    await this.run('equipping item', async () => {
      const knownItem = this.store.get().inventory?.find((entry) => entry.itemInstanceId === pending.itemInstanceId);
      await this.equipAndRefresh(pending.itemInstanceId, pending.slot, true, knownItem);
    });
  }

  private async equipAndRefresh(
    itemInstanceId: string,
    slot: EquipSlot,
    confirmBind: boolean,
    knownItem: InventoryItem | undefined,
  ): Promise<void> {
    if (confirmBind) {
      await this.client.equip(itemInstanceId, slot, true);
    } else {
      await this.client.equip(itemInstanceId, slot);
    }
    const { inventory, equipment } = await this.inventorySnapshot();
    const refreshedItem =
      inventory.items.find((entry) => entry.itemInstanceId === itemInstanceId) ??
      equipment.items.find((entry) => entry.itemInstanceId === itemInstanceId) ??
      knownItem;
    const confirmed = equipment.items.some(
      (entry) => entry.itemInstanceId === itemInstanceId && entry.equippedSlot === slot,
    );
    if (!confirmed) throw equipStateMismatchError(refreshedItem, slot);
    this.store.set({
      inventory: inventory.items,
      equipment,
      selectedInventoryItemId: inventorySelection(itemInstanceId, inventory.items),
      pendingBindEquip: null,
      notice: `Equipped into ${slotLabel(slot)}.`,
    });
  }

  async unequip(slot: EquipSlot): Promise<void> {
    await this.run('unequipping item', async () => {
      await this.client.unequip(slot);
      const { inventory, equipment } = await this.inventorySnapshot();
      this.store.set({
        inventory: inventory.items,
        equipment,
        selectedInventoryItemId: inventorySelection(this.store.get().selectedInventoryItemId, inventory.items),
        notice: `Cleared ${slotLabel(slot)}.`,
      });
    });
  }

  selectInventoryItem(itemInstanceId: string): void {
    const exists = this.store.get().inventory?.some((entry) => entry.itemInstanceId === itemInstanceId) ?? false;
    if (exists) this.store.set({ selectedInventoryItemId: itemInstanceId });
  }

  // ── waystation hub ────────────────────────────────────────────────────────────

  /** Enter (or re-enter) the Waystation: load presence + switch to the hub view. */
  async enterWaystation(opts: { swallow?: boolean } = {}): Promise<void> {
    await this.run('entering Waystation', () => this.loadWaystationHub(), { swallow: opts.swallow });
  }

  private async loadWaystationHub(): Promise<void> {
    const hub = await this.client.hubEnter();
    const self = hub.presence.find((entry) => entry.accountId === hub.self.accountId);
    const position = self && typeof self.x === 'number' && typeof self.y === 'number'
      ? { x: self.x, y: self.y }
      : spawnHubPosition(hub.self.accountId);
    const facing = self?.facing ?? 'south';
    this.store.set({
      hub,
      hubInspect: null,
      hubPosition: position,
      hubFacing: facing,
      hubNearbyStationId: nearbyStationForPosition(position, this.store.get().devMode)?.id ?? null,
      view: 'waystation',
    });
  }

  setHubLocalPosition(position: HubPosition, facing: HubFacing): void {
    this.store.set({
      hubPosition: position,
      hubFacing: facing,
      hubNearbyStationId: nearbyStationForPosition(position, this.store.get().devMode)?.id ?? null,
    });
  }

  moveHubLocal(direction: HubDirection): void {
    const state = this.store.get();
    if (state.view !== 'waystation' || !state.hub || state.busy) return;
    const current = localHubPosition(state);
    const moved = moveHubPosition(current.position, direction, state.devMode);
    this.store.set({
      hubPosition: moved.position,
      hubFacing: moved.facing,
      hubNearbyStationId: nearbyStationForPosition(moved.position, state.devMode)?.id ?? null,
    });
  }

  /** Manual roster refresh (visible busy/error). */
  async refreshPresence(): Promise<void> {
    await this.run('refreshing presence', async () => {
      const res = await this.client.hubPresence();
      const hub = this.store.get().hub;
      if (hub) this.store.set({ hub: { ...hub, presence: res.presence } });
    });
  }

  /** Quiet heartbeat used by the background interval — never raises busy/error. */
  private hubHeartbeatInFlight = false;

  async heartbeatHub(): Promise<void> {
    const s = this.store.get();
    if (!s.session || s.view !== 'waystation' || !s.hub) return;
    // One heartbeat at a time: on a slow network, overlapping requests can
    // resolve out of order and briefly rewind the roster (players flicker).
    if (this.hubHeartbeatInFlight) return;
    this.hubHeartbeatInFlight = true;
    const local = localHubPosition(s);
    try {
      const res = await this.client.hubHeartbeat({ x: local.position.x, y: local.position.y, facing: local.facing });
      const hub = this.store.get().hub;
      if (hub) this.store.set({ hub: { ...hub, access: res.access, reason: res.reason, presence: res.presence } });
    } catch {
      /* presence is best-effort; a dropped heartbeat must not disrupt the UI */
    } finally {
      this.hubHeartbeatInFlight = false;
    }
  }

  /** Client-side emote cooldown mirror — the backend enforces the same limit. */
  private lastEmoteSentAtMs = 0;

  async sendEmote(emote: string): Promise<void> {
    const nowMs = Date.now();
    if (nowMs - this.lastEmoteSentAtMs < 1_000) return;
    this.lastEmoteSentAtMs = nowMs;
    await this.run('emote: ' + emote, async () => {
      const res = await this.client.hubEmote(emote);
      const hub = this.store.get().hub;
      if (hub) {
        const selfId = hub.self.accountId;
        const presence = hub.presence.map((p) =>
          p.accountId === selfId
            ? { ...p, emote: res.emote, emoteStartedAt: res.startedAt, emoteExpiresAt: res.expiresAt }
            : p,
        );
        this.store.set({ hub: { ...hub, presence } });
      }
    });
  }

  async inspectPlayer(accountId: string): Promise<void> {
    await this.run('inspecting player', async () => {
      const hubInspect = await this.client.hubInspect(accountId);
      this.store.set({ hubInspect });
    });
  }

  clearInspect(): void {
    this.store.set({ hubInspect: null });
  }

  // ── marketplace (Phase G) ──────────────────────────────────────────────────────

  /** Open the Marketplace: load listings + my-listings + inventory, then switch view. */
  async openMarketplace(): Promise<void> {
    await this.run('opening Marketplace', async () => {
      await this.loadMarketplaceData();
      this.store.set({ view: 'marketplace' });
    });
  }

  /** Manual refresh of marketplace data (visible busy/error). */
  async refreshMarketplace(): Promise<void> {
    await this.run('refreshing Marketplace', async () => {
      await this.loadMarketplaceData();
    });
  }

  /**
   * List an owned sealed item for Gold or $OBOL. Gold keeps the existing 0%-fee
   * behavior; $OBOL takes a human-readable token price, converts it to raw
   * units with the mint decimals, and requires an exact 95/5 split.
   */
  async listItem(itemInstanceId: string, opts: { currency: 'gold' | 'obol'; price?: number; obolText?: string }): Promise<void> {
    if (opts.currency === 'gold') {
      const price = opts.price ?? NaN;
      if (!Number.isInteger(price) || price <= 0) {
        this.store.set({
          busy: null,
          notice: null,
          error: { status: 400, code: 'invalid_price', message: 'Enter a positive whole number of Gold.' },
        });
        return;
      }
      await this.run('listing item', async () => {
        await this.client.marketplaceList(itemInstanceId, { currency: 'gold', price });
        await this.loadMarketplaceData();
        this.store.set({ notice: `Listed for ${price} Gold.` });
      });
      return;
    }
    await this.run('listing item for $OBOL', async () => {
      const { raw, decimals } = await this.obolListingRawOrError(opts.obolText ?? '');
      await this.client.marketplaceList(itemInstanceId, { currency: 'obol', obolAmountTotal: raw.toString() });
      await this.loadMarketplaceData();
      this.store.set({ notice: `Listed for ${formatRawTokenAmount(raw, decimals)} $OBOL — buyer pays with wallet; you receive 95%, treasury 5%.` });
    });
  }

  /**
   * Convert a seller's human-readable $OBOL listing price into raw units and
   * validate the exact 95/5 split BEFORE any request. Same rules as the Gold
   * Exchange seller flow.
   */
  private async obolListingRawOrError(obolText: string): Promise<{ raw: bigint; decimals: number }> {
    const decimals = await this.cashoutDecimalsOrError();
    const raw = parseTokenAmountToRaw(obolText, decimals);
    if (raw === null || raw <= 0n) {
      throw new ApiError({
        status: 400,
        code: 'invalid_obol_price',
        message: `Enter a positive total $OBOL price${decimals > 0 ? ` with at most ${decimals} decimal places` : ''}.`,
      });
    }
    if (raw % 20n !== 0n) {
      throw new ApiError({
        status: 400,
        code: 'invalid_obol_price',
        message: 'This $OBOL price cannot split exactly into 95% seller / 5% treasury. Adjust the amount slightly — whole $OBOL amounts always split cleanly.',
      });
    }
    if (raw > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new ApiError({ status: 400, code: 'invalid_obol_price', message: 'This $OBOL price is too large to list.' });
    }
    return { raw, decimals };
  }

  /**
   * Buy an $OBOL-denominated listing with the connected wallet:
   * reserve a purchase intent → preflight the buyer's $OBOL balance (never
   * open Phantom on an unaffordable payment) → wallet payment → server-side
   * verification → the asset lands in the buyer's stash/collection.
   */
  async payObolListing(kind: 'gear' | 'cosmetic', listingId: string): Promise<void> {
    const payerWallet = this.boundWalletOrError();
    if (!payerWallet) return;
    await this.run('paying $OBOL listing with wallet', async () => {
      const turnstileToken = await this.turnstileToken('marketplace_intent_create');
      const intent = kind === 'gear'
        ? await this.client.marketplaceCreateIntent(listingId, turnstileToken)
        : await this.client.cosmeticMarketplaceCreateIntent(listingId, turnstileToken);
      await this.loadMarketplaceData();
      // A signature was ALREADY sent for this reservation (same session or
      // restored after refresh): never open Phantom again — that would risk a
      // second payment. Resume settlement with the stored signature instead.
      const sent = this.store.get().marketIntentPayments[intent.id];
      if (sent) {
        await this.settleMarketIntentAfterWalletSend(intent.id, sent.signature, kind);
        return;
      }
      const payment = intent.paymentInstructions;
      if (!payment) {
        throw new ApiError({ status: 409, code: 'payment_instructions_unavailable', message: 'Payment instructions are unavailable for this listing.' });
      }
      if (Date.parse(payment.expiresAt) <= Date.now()) {
        throw new ApiError({ status: 409, code: 'purchase_intent_expired', message: 'This Marketplace purchase reservation is outdated. Create a new purchase reservation.' });
      }
      let balance: SolanaTokenBalance;
      try {
        balance = await this.client.solanaTokenBalance(payment.mint, payerWallet);
      } catch {
        this.store.set({ marketWalletBalance: null, marketWalletBalanceError: true });
        throw new ApiError({ status: 503, code: 'obol_balance_unverified', message: 'Could not verify your $OBOL balance for this listing. Try again.' });
      }
      this.store.set({
        marketWalletBalance: { mint: balance.mint, amountRaw: balance.amountRaw, decimals: balance.decimals },
        marketWalletBalanceError: false,
      });
      if (BigInt(balance.amountRaw) < BigInt(payment.totalAmount)) {
        throw new ApiError({ status: 400, code: 'insufficient_obol_balance', message: 'Insufficient $OBOL for this listing.' });
      }
      const context = await this.client.solanaPaymentContext(payment.mint);
      this.store.set({ busy: 'opening wallet' });
      const signature = await this.solanaPayments().payMarketplace(payment, payerWallet, context);
      // From this moment the payment exists on-chain: remember the signature
      // (state + localStorage) BEFORE any settlement request can fail.
      this.rememberMarketIntentPayment(intent.id, signature, kind);
      await this.settleMarketIntentAfterWalletSend(intent.id, signature, kind);
    });
  }

  /** Submit an on-chain signature for an existing $OBOL purchase intent (manual fallback). */
  async submitMarketIntentTx(intentId: string, txSignature: string): Promise<void> {
    const signature = txSignature.trim();
    if (signature.length === 0) {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 400, code: 'missing_tx_signature', message: 'Enter a transaction signature.' },
      });
      return;
    }
    await this.run('verifying payment', async () => {
      const kind = this.marketIntentKind(intentId);
      // A manually pasted signature is only remembered when nothing is stored
      // yet — a typo must never overwrite a signature the wallet actually sent.
      if (!this.store.get().marketIntentPayments[intentId]) {
        this.rememberMarketIntentPayment(intentId, signature, kind);
      }
      await this.settleMarketIntentAfterWalletSend(intentId, signature, kind);
    });
  }

  /** "Retry settlement" on a pending purchase: reuse the stored signature — never reopen the wallet. */
  async retryMarketIntentSettlement(intentId: string): Promise<void> {
    const sent = this.store.get().marketIntentPayments[intentId];
    if (!sent) {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 409, code: 'no_stored_signature', message: 'No stored payment signature for this purchase — paste the transaction signature instead.' },
      });
      return;
    }
    await this.run('verifying payment', async () => {
      await this.settleMarketIntentAfterWalletSend(intentId, sent.signature, sent.listingKind);
    });
  }

  /** Drop a stored (unsettled) payment signature the buyer no longer wants tracked. */
  dismissMarketIntentPayment(intentId: string): void {
    this.clearMarketIntentPayment(intentId);
    this.store.set({ notice: 'Stored payment signature discarded.' });
  }

  /**
   * Settle a marketplace $OBOL purchase AFTER the wallet already sent the
   * payment. transaction_not_found / unconfirmed responses are normal Solana
   * propagation, not failure: keep retrying with backoff for the whole window,
   * and when the window closes leave the purchase pending with the signature
   * remembered and a "Retry settlement" affordance — never a dead end, never
   * a second wallet payment.
   */
  private async settleMarketIntentAfterWalletSend(intentId: string, signature: string, kind: 'gear' | 'cosmetic'): Promise<void> {
    if (this.marketSettleInFlight.has(intentId)) {
      this.store.set({ notice: 'Payment sent. Waiting for confirmation…' });
      return;
    }
    this.marketSettleInFlight.add(intentId);
    try {
      const delays = this.options.marketSubmitRetryDelaysMs ?? DEFAULT_MARKET_SUBMIT_RETRY_DELAYS_MS;
      this.setMarketIntentPaymentStatus(intentId, 'confirming');
      this.store.set({ busy: 'verifying payment' });
      for (let attempt = 0; ; attempt += 1) {
        try {
          await this.client.marketplaceSubmitIntentTx(intentId, signature);
          this.clearMarketIntentPayment(intentId);
          await this.loadMarketplaceData();
          this.store.set({
            notice: kind === 'gear'
              ? 'Purchase complete — the item is in your stash.'
              : 'Purchase complete — the cosmetic is in your collection.',
          });
          return;
        } catch (err) {
          const normalized = normalizeControllerError(err);
          if (normalized.code === 'tx_replayed') {
            // This signature already settled a purchase (e.g. an earlier
            // submit landed server-side, or another tab finished first). If
            // this intent is no longer payable, that settlement was ours.
            await this.loadMarketplaceData();
            const stillActive = (this.store.get().myMarketIntents ?? []).some((i) => i.id === intentId && i.status === 'active');
            if (!stillActive) {
              this.clearMarketIntentPayment(intentId);
              this.store.set({
                notice: kind === 'gear'
                  ? 'Purchase complete — the item is in your stash.'
                  : 'Purchase complete — the cosmetic is in your collection.',
              });
              return;
            }
          }
          if (!isRetriableMarketSettlement(normalized)) {
            // Hard failure (wrong mint/amount/recipient/reference, expired and
            // unrecoverable, …). Keep the signature remembered — retrying is
            // still offered from the pending card, and Phantom stays closed.
            this.setMarketIntentPaymentStatus(intentId, 'unconfirmed');
            throw new ApiError({
              status: normalized.status,
              code: normalized.code,
              requestId: normalized.requestId,
              details: { ...normalized.details, txSignature: signature },
              message: `${normalized.message} — your wallet payment was sent (signature ${signature.slice(0, 8)}…). Do not pay again; retry settlement from Pending $OBOL purchases.`,
            });
          }
          if (attempt >= delays.length) {
            // Window exhausted without confirmation: NOT a final failure.
            this.setMarketIntentPaymentStatus(intentId, 'unconfirmed');
            await this.loadMarketplaceData();
            this.store.set({ notice: 'Payment was sent but is not confirmed yet. Retry settlement from Pending $OBOL purchases.' });
            return;
          }
          this.store.set({
            busy: 'waiting for Solana confirmation',
            notice: `Payment sent. Waiting for confirmation… (${attempt + 1}/${delays.length + 1})`,
          });
          await sleep(delays[attempt]!);
        }
      }
    } finally {
      this.marketSettleInFlight.delete(intentId);
    }
  }

  /**
   * Merge signatures persisted in localStorage into the in-memory payment map
   * (page refresh mid-confirmation must not lose them). Restored entries come
   * back as 'unconfirmed' — no retry loop is running for them yet. Entries a
   * live loop owns keep their current status.
   */
  private hydrateMarketIntentPayments(): Record<string, MarketIntentPayment> {
    const payments = { ...this.store.get().marketIntentPayments };
    for (const [intentId, entry] of Object.entries(loadStoredMarketIntentTx())) {
      if (!payments[intentId]) {
        payments[intentId] = { signature: entry.signature, status: 'unconfirmed', listingKind: entry.listingKind };
      }
    }
    return payments;
  }

  private marketIntentKind(intentId: string): 'gear' | 'cosmetic' {
    return (this.store.get().myMarketIntents ?? []).find((i) => i.id === intentId)?.listingKind === 'cosmetic'
      ? 'cosmetic'
      : 'gear';
  }

  private rememberMarketIntentPayment(intentId: string, signature: string, listingKind: 'gear' | 'cosmetic'): void {
    const payments = { ...this.store.get().marketIntentPayments, [intentId]: { signature, status: 'confirming' as const, listingKind } };
    this.store.set({ marketIntentPayments: payments });
    const stored = loadStoredMarketIntentTx();
    stored[intentId] = { signature, listingKind, savedAt: Date.now() };
    saveStoredMarketIntentTx(stored);
  }

  private setMarketIntentPaymentStatus(intentId: string, status: MarketIntentPayment['status']): void {
    const payments = this.store.get().marketIntentPayments;
    const entry = payments[intentId];
    if (!entry || entry.status === status) return;
    this.store.set({ marketIntentPayments: { ...payments, [intentId]: { ...entry, status } } });
  }

  private clearMarketIntentPayment(intentId: string): void {
    const payments = { ...this.store.get().marketIntentPayments };
    delete payments[intentId];
    this.store.set({ marketIntentPayments: payments });
    const stored = loadStoredMarketIntentTx();
    delete stored[intentId];
    saveStoredMarketIntentTx(stored);
  }

  async cancelListing(listingId: string): Promise<void> {
    await this.run('cancelling listing', async () => {
      await this.client.marketplaceCancel(listingId);
      await this.loadMarketplaceData();
      this.store.set({ notice: 'Listing cancelled — item returned to your stash.' });
    });
  }

  async buyListing(listingId: string): Promise<void> {
    await this.run('buying listing', async () => {
      const res = await this.client.marketplaceBuy(listingId);
      await this.loadMarketplaceData();
      this.store.set({ notice: `Bought for ${res.pricePaid} Gold. Balance: ${res.buyerGoldBalance} Gold.` });
    });
  }

  /**
   * Fetch gear listings + my-listings (with Gold balance) + inventory, plus
   * the cosmetic marketplace data (player-to-player cosmetic listings now live
   * under the Marketplace Cosmetics category).
   */
  private async loadMarketplaceData(): Promise<void> {
    const [listingsRes, mine, inv, cosmeticListings, myCosmeticListings, myCosmetics, myIntents] = await Promise.all([
      this.client.marketplaceListings(),
      this.client.marketplaceMyListings(),
      this.client.inventory(),
      this.client.cosmeticListings(),
      this.client.myCosmeticListings(),
      this.client.myCosmetics(),
      Promise.resolve().then(() => this.client.marketplaceMyIntents()).catch(() => ({ intents: [] })),
    ]);
    this.store.set({
      marketListings: listingsRes.listings,
      myListings: mine.listings,
      goldBalance: mine.goldBalance,
      inventory: inv.items,
      cosmeticListings: cosmeticListings.listings,
      myCosmeticListings: myCosmeticListings.listings,
      myCosmetics: myCosmetics.owned,
      equippedCosmetics: myCosmetics.equipped,
      cosmeticsGoldBalance: myCosmetics.goldBalance,
      myMarketIntents: myIntents.intents,
      marketIntentPayments: this.hydrateMarketIntentPayments(),
    });
    await this.loadMarketWalletBalance();
  }

  /**
   * Best-effort wallet $OBOL balance so $OBOL listing cards can disable
   * "Pay with wallet" before Phantom ever opens. A failed read flags the
   * fail-closed state; the pay action re-checks authoritatively anyway.
   */
  private async loadMarketWalletBalance(): Promise<void> {
    const wallet = this.store.get().walletAddress ?? this.store.get().account?.wallet.wallet_address ?? null;
    const mint = this.store.get().marketListings?.find((l) => l.currency === 'obol')
      ? OBOL_CONTRACT_ADDRESS
      : (this.store.get().cosmeticListings?.some((l) => l.currency === 'obol') ? OBOL_CONTRACT_ADDRESS : null);
    if (!wallet || !mint) return;
    try {
      const balance = await this.client.solanaTokenBalance(mint, wallet);
      this.store.set({ marketWalletBalance: { mint: balance.mint, amountRaw: balance.amountRaw, decimals: balance.decimals }, marketWalletBalanceError: false });
    } catch {
      this.store.set({ marketWalletBalance: null, marketWalletBalanceError: true });
    }
  }

  /** Toggle which listing currencies are visible in the Marketplace. */
  setMarketCurrency(currency: string, enabled: boolean): void {
    if (currency !== 'gold' && currency !== 'obol') return;
    const current = this.store.get().marketCurrencies;
    this.store.set({ marketCurrencies: { ...current, [currency]: enabled } });
  }

  // Marketplace browse filters (category / rarity / sort / skin).
  setMarketCategory(category: string): void {
    if (category === 'weapon' || category === 'armor' || category === 'trinket' || category === 'lantern' || category === 'cosmetics') {
      this.store.set({ marketCategory: category });
    }
  }

  setMarketRarity(rarity: string): void {
    if (rarity === 'all' || rarity === 'common' || rarity === 'uncommon' || rarity === 'rare' || rarity === 'epic' || rarity === 'relic') {
      this.store.set({ marketRarity: rarity as MarketRarityFilter });
    }
  }

  setMarketSort(sort: string): void {
    if (sort === 'newest' || sort === 'price') {
      this.store.set({ marketSort: sort });
    }
  }

  setMarketSkin(skin: string): void {
    if (skin.length > 0) this.store.set({ marketSkin: skin });
  }

  // ── Gold cash-out / Gold Exchange (Phase H) ──────────────────────────────────

  /** Open the Gold Exchange: load active Gold sale orders + my orders. */
  async openCashout(): Promise<void> {
    await this.run('opening Gold Exchange', async () => {
      await this.loadCashoutData();
      this.store.set({ view: 'cashout' });
    });
  }

  async refreshCashout(): Promise<void> {
    await this.run('refreshing Gold Exchange', async () => {
      await this.loadCashoutData();
    });
  }

  /**
   * Create a Gold sale order. `obolAmountText` is the HUMAN-readable total
   * $OBOL price the seller typed ("10", "2.5") — it is converted to raw
   * smallest units with the cashout mint decimals before hitting the API, and
   * the 95/5 split is validated on those raw units. `expiryDays` is the
   * seller-chosen listing duration (1–30 whole days). Nothing is escrowed
   * until every validation passes (the server re-validates authoritatively).
   */
  async createCashoutOrder(goldAmount: number, obolAmountText: string, expiryDays: number): Promise<void> {
    const inputError = (message: string): void => {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 400, code: 'invalid_cashout_amount', message },
      });
    };
    if (!Number.isInteger(goldAmount) || goldAmount <= 0) {
      return inputError('Enter a positive whole Gold amount.');
    }
    if (!Number.isInteger(expiryDays) || expiryDays < 1 || expiryDays > 30) {
      return inputError('Listing duration must be a whole number of days between 1 and 30.');
    }
    await this.run('creating Gold order', async () => {
      const decimals = await this.cashoutDecimalsOrError();
      const raw = parseTokenAmountToRaw(obolAmountText, decimals);
      if (raw === null) {
        throw new ApiError({
          status: 400,
          code: 'invalid_cashout_amount',
          message: `Enter a positive total $OBOL price${decimals > 0 ? ` with at most ${decimals} decimal places` : ''}.`,
        });
      }
      if (raw % 20n !== 0n) {
        throw new ApiError({
          status: 400,
          code: 'invalid_cashout_amount',
          message: 'This $OBOL price cannot split exactly into 95% seller / 5% treasury. Adjust the amount slightly — whole $OBOL amounts always split cleanly.',
        });
      }
      if (raw > BigInt(Number.MAX_SAFE_INTEGER)) {
        throw new ApiError({ status: 400, code: 'invalid_cashout_amount', message: 'This $OBOL price is too large to list.' });
      }
      await this.client.cashoutCreateOrder(goldAmount, Number(raw), expiryDays, await this.turnstileToken('cashout_order_create'));
      await this.loadCashoutData();
      this.store.set({
        notice: `Listed ${goldAmount} Gold for ${formatRawTokenAmount(raw, decimals)} $OBOL — expires in ${expiryDays} day${expiryDays === 1 ? '' : 's'}.`,
      });
    });
  }

  /**
   * Mint decimals needed to convert the seller's human $OBOL price to raw
   * units. Uses the cached Exchange decimals, otherwise resolves them via the
   * payment-context endpoint (order mint when one exists, public $OBOL mint
   * otherwise). Fails closed — no escrow happens on an unknown conversion.
   */
  private async cashoutDecimalsOrError(): Promise<number> {
    const cached = this.store.get().cashoutTokenDecimals;
    if (cached !== null) return cached;
    const mint = this.store.get().cashoutOrders?.[0]?.expectedMint
      ?? this.store.get().myCashoutOrders?.[0]?.expectedMint
      ?? OBOL_CONTRACT_ADDRESS;
    try {
      const context = await this.client.solanaPaymentContext(mint);
      this.store.set({ cashoutTokenDecimals: context.decimals });
      return context.decimals;
    } catch {
      throw new ApiError({
        status: 503,
        code: 'obol_decimals_unavailable',
        message: 'Could not load $OBOL mint decimals to convert your price. Try again.',
      });
    }
  }

  async cancelCashoutOrder(orderId: string): Promise<void> {
    await this.run('cancelling Gold order', async () => {
      await this.client.cashoutCancelOrder(orderId);
      await this.loadCashoutData();
      this.store.set({ notice: 'Gold sale order cancelled — escrow released.' });
    });
  }

  /**
   * Buy a Gold Exchange order with the wallet (audit1 PAY-1): reserve the
   * order with a buyer-specific purchase intent, preflight the $OBOL balance
   * BEFORE Phantom ever opens, pay from the intent's instructions, and settle
   * with the marketplace-style retry loop. A signature already sent for this
   * reservation is always resumed — Phantom is never reopened for it.
   */
  async payCashoutWithWallet(orderId: string): Promise<void> {
    const payerWallet = this.boundWalletOrError();
    if (!payerWallet) return;
    await this.run('reserving Gold order', async () => {
      const turnstileToken = await this.turnstileToken('cashout_order_create');
      const intent = await this.client.cashoutCreateIntent(orderId, turnstileToken);
      await this.loadCashoutData();
      const sent = this.store.get().cashoutIntentPayments[intent.id];
      if (sent) {
        await this.settleCashoutIntentAfterWalletSend(intent.id, sent.signature);
        return;
      }
      const payment = intent.paymentInstructions;
      if (!payment) {
        throw new ApiError({ status: 409, code: 'payment_instructions_unavailable', message: 'Payment instructions are unavailable for this order.' });
      }
      if (Date.parse(payment.expiresAt) <= Date.now()) {
        throw new ApiError({ status: 409, code: 'cashout_intent_expired', message: 'This Gold Exchange reservation is outdated. Reserve the order again.' });
      }
      // Block before Phantom when the wallet cannot afford the payment.
      let balance: SolanaTokenBalance;
      try {
        balance = await this.client.solanaTokenBalance(payment.mint, payerWallet);
      } catch {
        throw new ApiError({ status: 503, code: 'obol_balance_unverified', message: 'Could not verify your $OBOL balance for this order. Try again.' });
      }
      if (BigInt(balance.amountRaw) < BigInt(payment.totalAmount)) {
        throw new ApiError({ status: 400, code: 'insufficient_obol_balance', message: 'Insufficient $OBOL for this Gold order.' });
      }
      const context = await this.client.solanaPaymentContext(payment.mint);
      this.store.set({ busy: 'opening wallet' });
      const signature = await this.solanaPayments().payCashout(payment, payerWallet, context);
      // From this moment the payment exists on-chain: remember the signature
      // (state + localStorage) BEFORE any settlement request can fail.
      this.rememberCashoutIntentPayment(intent.id, signature);
      await this.settleCashoutIntentAfterWalletSend(intent.id, signature);
    });
  }

  /** Submit an on-chain signature for an existing Gold Exchange intent (manual fallback). */
  async submitCashoutIntentTx(intentId: string, txSignature: string): Promise<void> {
    const signature = txSignature.trim();
    if (signature.length === 0) {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 400, code: 'missing_tx_signature', message: 'Enter a transaction signature.' },
      });
      return;
    }
    await this.run('verifying payment', async () => {
      // A manually pasted signature is only remembered when nothing is stored
      // yet — a typo must never overwrite a signature the wallet actually sent.
      if (!this.store.get().cashoutIntentPayments[intentId]) {
        this.rememberCashoutIntentPayment(intentId, signature);
      }
      await this.settleCashoutIntentAfterWalletSend(intentId, signature);
    });
  }

  /** "Retry settlement" on a pending Gold purchase: reuse the stored signature — never reopen the wallet. */
  async retryCashoutIntentSettlement(intentId: string): Promise<void> {
    const sent = this.store.get().cashoutIntentPayments[intentId];
    if (!sent) {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 409, code: 'no_stored_signature', message: 'No stored payment signature for this purchase — paste the transaction signature instead.' },
      });
      return;
    }
    await this.run('verifying payment', async () => {
      await this.settleCashoutIntentAfterWalletSend(intentId, sent.signature);
    });
  }

  /** Drop a stored (unsettled) Gold Exchange payment signature. */
  dismissCashoutIntentPayment(intentId: string): void {
    this.clearCashoutIntentPayment(intentId);
    this.store.set({ notice: 'Stored payment signature discarded.' });
  }

  /**
   * Settle a Gold Exchange purchase AFTER the wallet already sent the payment
   * — same contract as the marketplace settle loop: propagation delays keep
   * retrying, a closed window leaves the purchase pending with the signature
   * remembered, and Phantom is never reopened.
   */
  private async settleCashoutIntentAfterWalletSend(intentId: string, signature: string): Promise<void> {
    if (this.cashoutSettleInFlight.has(intentId)) {
      this.store.set({ notice: 'Payment sent. Waiting for confirmation…' });
      return;
    }
    this.cashoutSettleInFlight.add(intentId);
    try {
      const delays = this.options.cashoutSubmitRetryDelaysMs ?? DEFAULT_MARKET_SUBMIT_RETRY_DELAYS_MS;
      this.setCashoutIntentPaymentStatus(intentId, 'confirming');
      this.store.set({ busy: 'verifying payment' });
      for (let attempt = 0; ; attempt += 1) {
        try {
          const res = await this.client.cashoutSubmitIntentTx(intentId, signature);
          this.clearCashoutIntentPayment(intentId);
          await this.loadCashoutData();
          this.store.set({ notice: `Received ${res.goldCredited} Gold. Balance: ${res.buyerGoldBalance} Gold.` });
          return;
        } catch (err) {
          const normalized = normalizeControllerError(err);
          if (normalized.code === 'tx_replayed') {
            // This signature already settled (earlier submit landed server-side
            // or another tab finished). If the intent is no longer payable,
            // that settlement was ours.
            await this.loadCashoutData();
            const stillActive = (this.store.get().myCashoutIntents ?? []).some((i) => i.id === intentId && i.status === 'active');
            if (!stillActive) {
              this.clearCashoutIntentPayment(intentId);
              this.store.set({ notice: 'Purchase complete — the Gold is in your balance.' });
              return;
            }
          }
          if (!isRetriableMarketSettlement(normalized)) {
            this.setCashoutIntentPaymentStatus(intentId, 'unconfirmed');
            throw new ApiError({
              status: normalized.status,
              code: normalized.code,
              requestId: normalized.requestId,
              details: { ...normalized.details, txSignature: signature },
              message: `${normalized.message} — your wallet payment was sent (signature ${signature.slice(0, 8)}…). Do not pay again; retry settlement from Pending Gold purchases.`,
            });
          }
          if (attempt >= delays.length) {
            this.setCashoutIntentPaymentStatus(intentId, 'unconfirmed');
            await this.loadCashoutData();
            this.store.set({ notice: 'Payment was sent but is not confirmed yet. Retry settlement from Pending Gold purchases.' });
            return;
          }
          this.store.set({
            busy: 'waiting for Solana confirmation',
            notice: `Payment sent. Waiting for confirmation… (${attempt + 1}/${delays.length + 1})`,
          });
          await sleep(delays[attempt]!);
        }
      }
    } finally {
      this.cashoutSettleInFlight.delete(intentId);
    }
  }

  /** Merge localStorage-persisted Gold Exchange signatures back in after a refresh. */
  private hydrateCashoutIntentPayments(): Record<string, CashoutIntentPayment> {
    const payments = { ...this.store.get().cashoutIntentPayments };
    for (const [intentId, entry] of Object.entries(loadStoredCashoutIntentTx())) {
      if (!payments[intentId]) payments[intentId] = { signature: entry.signature, status: 'unconfirmed' };
    }
    return payments;
  }

  private rememberCashoutIntentPayment(intentId: string, signature: string): void {
    const payments = { ...this.store.get().cashoutIntentPayments, [intentId]: { signature, status: 'confirming' as const } };
    this.store.set({ cashoutIntentPayments: payments });
    const stored = loadStoredCashoutIntentTx();
    stored[intentId] = { signature, savedAt: Date.now() };
    saveStoredCashoutIntentTx(stored);
  }

  private setCashoutIntentPaymentStatus(intentId: string, status: CashoutIntentPayment['status']): void {
    const payments = this.store.get().cashoutIntentPayments;
    const entry = payments[intentId];
    if (!entry || entry.status === status) return;
    this.store.set({ cashoutIntentPayments: { ...payments, [intentId]: { ...entry, status } } });
  }

  private clearCashoutIntentPayment(intentId: string): void {
    const payments = { ...this.store.get().cashoutIntentPayments };
    delete payments[intentId];
    this.store.set({ cashoutIntentPayments: payments });
    const stored = loadStoredCashoutIntentTx();
    delete stored[intentId];
    saveStoredCashoutIntentTx(stored);
  }

  private async loadCashoutData(): Promise<void> {
    const [orders, mine, myIntents] = await Promise.all([
      this.client.cashoutOrders(),
      this.client.cashoutMyOrders(),
      Promise.resolve().then(() => this.client.cashoutMyIntents()).catch(() => ({ intents: [] })),
    ]);
    this.store.set({
      cashoutOrders: orders.orders,
      myCashoutOrders: mine.orders,
      cashoutGoldBalance: mine.goldBalance,
      myCashoutIntents: myIntents.intents,
      cashoutIntentPayments: this.hydrateCashoutIntentPayments(),
    });
    await this.loadCashoutTokenDecimals(orders.orders[0]?.expectedMint ?? mine.orders[0]?.expectedMint);
  }

  /**
   * Best-effort $OBOL mint decimals for the Gold/$OBOL rate display. Sorting
   * works on raw amounts either way; only the human-readable rate needs this,
   * so failures leave the rate hidden instead of blocking the Exchange.
   */
  private async loadCashoutTokenDecimals(mint: string | undefined): Promise<void> {
    if (!mint || this.store.get().cashoutTokenDecimals !== null) return;
    try {
      const context = await this.client.solanaPaymentContext(mint);
      this.store.set({ cashoutTokenDecimals: context.decimals });
    } catch {
      // rate display degrades gracefully without decimals
    }
  }

  setCashoutSort(sort: string): void {
    if (sort === 'rate' || sort === 'gold' || sort === 'newest') {
      this.store.set({ cashoutSort: sort });
    }
  }

  // ── Spinner / Ferryman's Wager (Phase I) ─────────────────────────────────────

  /** Open the Spinner station: load status (cadence, price, odds, pending orders). */
  async openSpinner(): Promise<void> {
    await this.run('opening the Ferryman’s Wager', async () => {
      this.store.set({ view: 'spinner', ...(await this.spinnerStatePatch()), lastSpinReward: null });
    });
  }

  async refreshSpinner(): Promise<void> {
    await this.run('refreshing the spinner', async () => {
      this.store.set(await this.spinnerStatePatch());
    });
  }

  async freeSpin(): Promise<void> {
    await this.run('taking your free spin', async () => {
      const result = await this.client.spinnerFreeSpin();
      this.store.set({ ...(await this.spinnerStatePatch()), lastSpinReward: result.reward, notice: describeReward('Free spin', result.reward) });
    });
  }

  async createSpinnerOrder(): Promise<void> {
    await this.run('creating a paid spin order', async () => {
      const order = await this.client.spinnerCreateOrder(await this.turnstileToken('spinner_order_create'));
      const usd = order.targetUsdCents !== null ? `$${(order.targetUsdCents / 100).toFixed(2)}` : '$5';
      this.store.set({
        ...(await this.spinnerStatePatch()),
        notice: `Paid spin quote created — pay the quoted ${usd}-in-$OBOL amount before the quote expires to spin.`,
      });
    });
  }

  async cancelSpinnerOrder(orderId: string): Promise<void> {
    await this.run('cancelling the spin order', async () => {
      await this.client.spinnerCancelOrder(orderId);
      this.store.set({ ...(await this.spinnerStatePatch()), notice: 'Spin order cancelled.' });
    });
  }

  /**
   * Spinner status + the wallet's $OBOL balance snapshot for the paid-quote
   * preflight. The balance is only fetched while a payable pending order
   * exists; a failed fetch flags `spinnerWalletBalanceError` so the paid
   * "Pay with wallet" control fails closed instead of opening the wallet.
   */
  private async spinnerStatePatch(): Promise<Partial<AppState>> {
    const spinner = await this.client.spinnerStatus();
    const patch: Partial<AppState> = { spinner, spinnerWalletBalance: null, spinnerWalletBalanceError: false };
    const mint = spinner.pendingOrders.find((o) => o.paymentInstructions)?.paymentInstructions?.mint;
    const wallet = this.store.get().walletAddress ?? this.store.get().account?.wallet.wallet_address ?? null;
    if (mint && wallet) {
      try {
        const balance = await this.client.solanaTokenBalance(mint, wallet);
        patch.spinnerWalletBalance = { mint: balance.mint, amountRaw: balance.amountRaw, decimals: balance.decimals };
      } catch {
        patch.spinnerWalletBalanceError = true;
      }
    }
    return patch;
  }

  async submitSpinnerTx(orderId: string, txSignature: string): Promise<void> {
    if (txSignature.trim().length === 0) {
      this.store.set({
        busy: null,
        notice: null,
        error: { status: 400, code: 'missing_tx_signature', message: 'Enter a transaction signature.' },
      });
      return;
    }
    await this.run('verifying payment & spinning', async () => {
      const res = await this.client.spinnerSubmitTx(orderId, txSignature.trim());
      this.store.set({ ...(await this.spinnerStatePatch()), lastSpinReward: res.reward, notice: describeReward('Paid spin', res.reward) });
    });
  }

  async paySpinnerWithWallet(orderId: string): Promise<void> {
    const order = this.store.get().spinner?.pendingOrders.find((o) => o.id === orderId);
    const payment = order?.paymentInstructions;
    const payerWallet = this.boundWalletOrError();
    if (!payment || !payerWallet) {
      if (!payment) {
        this.store.set({
          busy: null,
          notice: null,
          error: { status: 409, code: 'payment_instructions_unavailable', message: 'Payment instructions are unavailable for this spin order.' },
        });
      }
      return;
    }
    await this.run('paying with wallet & spinning', async () => {
      // Preflight BEFORE any wallet popup: verify the connected wallet can pay
      // the EXACT stored quote. An unaffordable or unverifiable quote never
      // reaches Phantom (whose failed simulation reads as a scam warning).
      let balance: SolanaTokenBalance;
      try {
        balance = await this.client.solanaTokenBalance(payment.mint, payerWallet);
      } catch {
        this.store.set({ spinnerWalletBalance: null, spinnerWalletBalanceError: true });
        throw new ApiError({
          status: 503,
          code: 'obol_balance_unverified',
          message: 'Could not verify $OBOL balance for this quote. Try again.',
        });
      }
      this.store.set({
        spinnerWalletBalance: { mint: balance.mint, amountRaw: balance.amountRaw, decimals: balance.decimals },
        spinnerWalletBalanceError: false,
      });
      if (BigInt(balance.amountRaw) < BigInt(payment.totalAmount)) {
        const decimals = order?.tokenDecimals ?? balance.decimals;
        throw new ApiError({
          status: 400,
          code: 'insufficient_obol_balance',
          message: `Insufficient $OBOL for this quote. Need ${formatRawTokenAmount(BigInt(payment.totalAmount), decimals)} $OBOL, wallet has ${formatRawTokenAmount(balance.amountRaw, decimals)} $OBOL.`,
        });
      }
      const context = await this.client.solanaPaymentContext(payment.mint);
      const signature = await this.solanaPayments().paySpinner(payment, payerWallet, context);
      const res = await this.submitSpinnerTxAfterWalletSend(orderId, signature);
      this.store.set({ ...(await this.spinnerStatePatch()), lastSpinReward: res.reward, notice: describeReward('Paid spin', res.reward) });
    });
  }

  // ── cosmetics (Phase K) ─────────────────────────────────────────────────────

  async openCosmetics(): Promise<void> {
    await this.run('opening Cosmetics', async () => {
      await this.loadCosmeticsData();
      this.store.set({ view: 'cosmetics' });
    });
  }

  async refreshCosmetics(): Promise<void> {
    await this.run('refreshing Cosmetics', async () => {
      await this.loadCosmeticsData();
    });
  }

  async buyCosmetic(cosmeticId: string): Promise<void> {
    await this.run('buying cosmetic', async () => {
      const mine = await this.client.cosmeticBuy(cosmeticId);
      await this.loadCosmeticsData();
      this.store.set({ notice: `Cosmetic purchased. Balance: ${mine.goldBalance} Gold.` });
    });
  }

  async claimFounderCosmetic(): Promise<void> {
    await this.claimCosmeticCampaign(FOUNDER_CLAIM_CAMPAIGN_ID);
  }

  async claimCosmeticCampaign(campaignId: string): Promise<void> {
    await this.run('claiming cosmetic', async () => {
      const res = await this.client.cosmeticClaimCampaign(campaignId, await this.turnstileToken('cosmetic_claim'));
      await this.loadCosmeticsData();
      const title = res.status.title || 'Cosmetic';
      this.store.set({
        myCosmetics: res.collection.owned,
        equippedCosmetics: res.collection.equipped,
        cosmeticsGoldBalance: res.collection.goldBalance,
        hub: await this.client.hubEnter(),
        notice: `${title} claimed.`,
      });
    });
  }

  async equipCosmetic(cosmeticId: string): Promise<void> {
    await this.run('equipping cosmetic', async () => {
      await this.client.cosmeticEquip(cosmeticId);
      await this.loadCosmeticsData();
      this.store.set({ hub: await this.client.hubEnter(), notice: 'Equipped cosmetic.' });
    });
  }

  async unequipCosmetic(cosmeticId: string): Promise<void> {
    await this.run('unequipping cosmetic', async () => {
      await this.client.cosmeticUnequip(cosmeticId);
      await this.loadCosmeticsData();
      this.store.set({ hub: await this.client.hubEnter(), notice: 'Unequipped cosmetic.' });
    });
  }

  async listCosmetic(cosmeticId: string, opts: { currency: 'gold' | 'obol'; price?: number; obolText?: string }): Promise<void> {
    if (opts.currency === 'gold') {
      const price = opts.price ?? NaN;
      if (!Number.isInteger(price) || price <= 0) {
        this.store.set({
          busy: null,
          notice: null,
          error: { status: 400, code: 'invalid_price', message: 'Enter a positive whole number of Gold.' },
        });
        return;
      }
      await this.run('listing cosmetic', async () => {
        await this.client.cosmeticList(cosmeticId, { currency: 'gold', price });
        await this.refreshCosmeticTradeData();
        this.store.set({ notice: `Listed for ${price} Gold.` });
      });
      return;
    }
    await this.run('listing cosmetic for $OBOL', async () => {
      const { raw, decimals } = await this.obolListingRawOrError(opts.obolText ?? '');
      await this.client.cosmeticList(cosmeticId, { currency: 'obol', obolAmountTotal: raw.toString() });
      await this.refreshCosmeticTradeData();
      this.store.set({ notice: `Listed for ${formatRawTokenAmount(raw, decimals)} $OBOL — buyer pays with wallet; you receive 95%, treasury 5%.` });
    });
  }

  async cancelCosmeticListing(listingId: string): Promise<void> {
    await this.run('cancelling cosmetic listing', async () => {
      await this.client.cosmeticListingCancel(listingId);
      await this.refreshCosmeticTradeData();
      this.store.set({ notice: 'Cosmetic listing cancelled — cosmetic unlocked.' });
    });
  }

  async buyCosmeticListing(listingId: string): Promise<void> {
    await this.run('buying cosmetic listing', async () => {
      const res = await this.client.cosmeticListingBuy(listingId);
      await this.refreshCosmeticTradeData();
      this.store.set({ notice: `Bought cosmetic for ${res.pricePaid} Gold. Balance: ${res.buyerGoldBalance} Gold.` });
    });
  }

  /**
   * Cosmetic trading now lives under the Marketplace Cosmetics category, so
   * cosmetic list/cancel/buy refresh whichever surface the player is on.
   */
  private async refreshCosmeticTradeData(): Promise<void> {
    if (this.store.get().view === 'marketplace') await this.loadMarketplaceData();
    else await this.loadCosmeticsData();
  }

  private async loadCosmeticsData(): Promise<void> {
    const [shop, mine, listings, myListings, claimCampaigns] = await Promise.all([
      this.client.cosmeticsShop(),
      this.client.myCosmetics(),
      this.client.cosmeticListings(),
      this.client.myCosmeticListings(),
      this.loadClaimCampaignStatuses(),
    ]);
    const founderClaim = claimCampaigns.find((campaign) => campaign.campaignId === FOUNDER_CLAIM_CAMPAIGN_ID) as CosmeticFounderClaimStatus | undefined;
    this.store.set({
      cosmeticsShop: shop.cosmetics,
      myCosmetics: mine.owned,
      equippedCosmetics: mine.equipped,
      cosmeticListings: listings.listings,
      myCosmeticListings: myListings.listings,
      cosmeticClaimCampaigns: claimCampaigns,
      cosmeticFounderClaim: founderClaim ?? null,
      cosmeticsGoldBalance: mine.goldBalance,
    });
  }

  private async loadEquippedCosmeticsForRun(): Promise<void> {
    try {
      const mine = await this.client.myCosmetics();
      this.store.set({
        myCosmetics: mine.owned,
        equippedCosmetics: mine.equipped,
        cosmeticsGoldBalance: mine.goldBalance,
      });
    } catch {
      // Cosmetic visuals are best-effort for run entry; gameplay remains server-authoritative.
    }
  }

  private async loadFounderClaimStatus(): Promise<CosmeticFounderClaimStatus | null> {
    try {
      return await this.client.cosmeticFounderClaimStatus();
    } catch {
      return null;
    }
  }

  private async loadClaimCampaignStatuses(): Promise<CosmeticClaimCampaignStatus[]> {
    try {
      return (await this.client.cosmeticClaimCampaignStatuses()).campaigns;
    } catch {
      const founder = await this.loadFounderClaimStatus();
      return founder ? [founder] : [];
    }
  }

  // ── spectate / non-holder funnel (Phase J) ───────────────────────────────────

  /** Return to the public landing/home screen. */
  showLanding(): void {
    this.store.set({ view: 'landing', spectateSession: null });
    this.options.onSpectateReset?.();
  }

  /** Open the public static docs screen without requiring authentication. */
  showDocs(): void {
    this.store.set({ view: 'docs', spectateSession: null });
    this.options.onSpectateReset?.();
  }

  /** Open the public How To Play beginner guide without requiring authentication. */
  showHowToPlay(): void {
    this.store.set({ view: 'how-to-play', spectateSession: null });
    this.options.onSpectateReset?.();
  }

  /** Open the public player-facing Roadmap without requiring authentication. */
  showRoadmap(): void {
    this.store.set({ view: 'roadmap', spectateSession: null });
    this.options.onSpectateReset?.();
  }

  /** Open the public Spectate screen: load the demo run list + Waystation teaser. */
  async openSpectate(): Promise<void> {
    this.store.set({ view: 'spectate', spectateSession: null });
    this.options.onSpectateReset?.();
    await this.run('loading spectate runs', async () => {
      const [runs, preview] = await Promise.all([
        this.client.spectateRuns(),
        this.client.spectateWaystationPreview(),
      ]);
      this.store.set({ spectateRuns: runs.runs, spectatePreview: preview });
    });
  }

  /** Open one curated replay (read-only): fetch its frames and start at the first. */
  async openReplay(id: string): Promise<void> {
    await this.run('loading replay', async () => {
      const { run, frames } = await this.client.spectateEvents(id);
      const session: SpectateSession = { summary: run, frames, index: 0, playing: false };
      this.store.set({ view: 'spectate', spectateSession: session });
      this.options.onSpectateReset?.();
    });
  }

  /** Leave the replay player and return to the demo run list. */
  closeReplay(): void {
    this.store.set({ spectateSession: null });
    this.options.onSpectateReset?.();
  }

  /** Step the open replay by `delta`, clamped; stops auto-play at the end. */
  spectateStep(delta: number): void {
    const session = this.store.get().spectateSession;
    if (!session) return;
    const index = Math.max(0, Math.min(session.frames.length - 1, session.index + delta));
    const playing = session.playing && index < session.frames.length - 1;
    this.store.set({ spectateSession: { ...session, index, playing } });
    if (index === session.index) return;
    const prev = session.frames[session.index] ?? null;
    const current = session.frames[index];
    if (delta > 0 && current) this.options.onSpectateFrame?.(prev, current);
    else this.options.onSpectateReset?.();
  }

  /** Jump the open replay back to the first frame. */
  spectateReset(): void {
    const session = this.store.get().spectateSession;
    if (!session) return;
    this.store.set({ spectateSession: { ...session, index: 0, playing: false } });
    this.options.onSpectateReset?.();
  }

  spectateSetPlaying(playing: boolean): void {
    const session = this.store.get().spectateSession;
    if (!session) return;
    // Pressing play at the end restarts from the beginning.
    const atEnd = session.index >= session.frames.length - 1;
    const index = playing && atEnd ? 0 : session.index;
    this.store.set({ spectateSession: { ...session, playing, index } });
    if (index !== session.index) this.options.onSpectateReset?.();
  }

  /** Auto-play tick: advance one frame while playing, driven by an interval in main.ts. */
  spectateTick(): void {
    const session = this.store.get().spectateSession;
    if (!session || !session.playing) return;
    this.spectateStep(1);
  }

  setView(view: AppState['view']): void {
    this.store.set({ view });
  }

  dismissError(): void {
    this.store.set({ error: null });
  }

  // ── internals ────────────────────────────────────────────────────────────────

  private adminTokenOrError(): string | null {
    const token = this.store.get().adminToken;
    if (token) return token;
    this.store.set({
      busy: null,
      notice: null,
      error: { status: 401, code: 'admin_auth_required', message: 'Enter an admin API token.' },
    });
    return null;
  }

  private async setAdminAbuseStatus(id: number, status: 'reviewed' | 'dismissed' | 'flagged'): Promise<void> {
    const token = this.adminTokenOrError();
    if (!token) return;
    await this.run(`marking signal ${status}`, async () => {
      const res =
        status === 'reviewed'
          ? await this.client.adminReviewAbuse(token, id)
          : status === 'dismissed'
            ? await this.client.adminDismissAbuse(token, id)
            : await this.client.adminFlagAbuse(token, id);
      const current = this.store.get().adminAbuseSignals ?? [];
      const selected = this.store.get().adminSelectedAbuseSignal;
      this.store.set({
        adminAbuseSignals: replaceAbuseSignalRecord(current, res.signal),
        adminSelectedAbuseSignal: selected?.id === res.signal.id ? res.signal : selected,
        notice: `Signal ${res.signal.id} marked ${res.signal.status}.`,
      });
    });
  }

  private solanaPayments(): SolanaWalletPayments {
    return {
      payCashout: this.options.solanaPayments?.payCashout ?? browserSolanaPayments.payCashout,
      payMarketplace: this.options.solanaPayments?.payMarketplace ?? browserSolanaPayments.payMarketplace,
      paySpinner: this.options.solanaPayments?.paySpinner ?? browserSolanaPayments.paySpinner,
    };
  }

  private async submitSpinnerTxAfterWalletSend(orderId: string, signature: string) {
    const delays = this.options.spinnerSubmitRetryDelaysMs ?? DEFAULT_SPINNER_SUBMIT_RETRY_DELAYS_MS;
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await this.client.spinnerSubmitTx(orderId, signature);
      } catch (err) {
        if (!(err instanceof ApiError) || !isRetryableSolanaVerification(err) || attempt >= delays.length) {
          throw err;
        }
        this.store.set({ notice: `Waiting for Solana confirmation (${attempt + 1}/${delays.length})...` });
        await sleep(delays[attempt]!);
      }
    }
  }

  private boundWalletOrError(): string | null {
    const wallet = this.store.get().walletAddress ?? this.store.get().account?.wallet.wallet_address ?? null;
    if (wallet) return wallet;
    this.store.set({
      busy: null,
      notice: null,
      error: { status: 409, code: 'wallet_required', message: 'Connect the wallet bound to this account before paying.' },
    });
    return null;
  }

  /**
   * Fresh single-use Turnstile token for one protected action; null when no
   * provider is configured (dev/test). Provider failures (script blocked,
   * challenge failed) throw with user-facing copy and surface in the error
   * banner via run() — the protected call is then never sent.
   */
  private async turnstileToken(action: string): Promise<string | null> {
    if (!this.options.turnstile) return null;
    return this.options.turnstile.getToken(action);
  }

  private async run(
    label: string,
    fn: () => Promise<void>,
    opts: { onAuthError?: () => void; swallow?: boolean } = {},
  ): Promise<void> {
    this.store.set({ busy: label, error: null, notice: null });
    try {
      await fn();
      this.store.set({ busy: null });
    } catch (err) {
      const normalized = normalizeControllerError(err);
      if (normalized.status === 401 && opts.onAuthError) opts.onAuthError();
      this.store.set({ busy: null, error: opts.swallow ? null : normalized });
    }
  }
}

function replaceAdminConfigRecord(config: AdminConfigRecord[], updated: AdminConfigRecord): AdminConfigRecord[] {
  const found = config.some((record) => record.key === updated.key);
  if (!found) return [...config, updated];
  return config.map((record) => (record.key === updated.key ? updated : record));
}

function replaceAbuseSignalRecord(signals: AbuseSignalRecord[], updated: AbuseSignalRecord): AbuseSignalRecord[] {
  const found = signals.some((record) => record.id === updated.id);
  if (!found) return [updated, ...signals];
  return signals.map((record) => (record.id === updated.id ? updated : record));
}

/** Human-readable notice for a spin result (the server is the reward authority). */
function describeReward(label: string, reward: SpinReward): string {
  if (reward.type === 'supplies' && reward.supplies) {
    const names = { oil: 'Oil Flask', torch: 'Torch Bundle', potion: 'Healing Draught' } as const;
    const parts = (['oil', 'torch', 'potion'] as const)
      .filter((key) => reward.supplies![key] > 0)
      .map((key) => `${reward.supplies![key]}× ${names[key]}`);
    return `${label}: won ${parts.join(' + ')} (added to your supply stock)!`;
  }
  if (reward.type === 'cosmetic' && reward.cosmetic) {
    return `${label}: won the weekly spin-exclusive cosmetic “${reward.cosmetic.title}”!`;
  }
  return `${label}: reward granted.`;
}

function isRetryableSolanaVerification(error: ApiError): boolean {
  if (error.code !== 'tx_rejected') return false;
  const verifierCode = error.details?.verifier_code;
  return verifierCode === 'transaction_not_found' || verifierCode === 'unconfirmed';
}

/**
 * Marketplace settlement responses that mean "the payment may just not be
 * visible at the requested commitment yet" — keep waiting, not a failure.
 * Covers the structured `retriable` flag, the verifier_code detail, a bare
 * verifier code from an older backend, and a temporarily unavailable verifier.
 */
function isRetriableMarketSettlement(error: ApiError): boolean {
  if (error.details?.retriable === true) return true;
  if (error.code === 'verifier_unavailable' || error.status === 0) return true;
  const verifierCode = error.details?.verifier_code ?? error.code;
  return verifierCode === 'transaction_not_found' || verifierCode === 'unconfirmed';
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
